// Offline queue persisted in localStorage so submissions survive page refresh on
// shared kiosk devices. Threat model: the kiosk browser profile must be locked
// down (no extensions, cleared storage on logout, no general browsing). XSS on
// the same origin could read queued data; mitigated by origin isolation and the
// locked-down browser profile. See architecture.md §3.3 kiosk deployment notes.
const QUEUE_KEY = 'kiosk-offline-queue';

export type QueuedCompletion = {
  id: string;
  zoneIds: string[];
  equipmentId: string;
  workedOn: string;
  attributedUserId: string;
  attempts: number;
};

export function enqueue(item: Omit<QueuedCompletion, 'id' | 'attempts'>): void {
  const queue = getQueue();
  queue.push({ ...item, id: crypto.randomUUID(), attempts: 0 });
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
}

export function getQueue(): QueuedCompletion[] {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]') as QueuedCompletion[];
  } catch {
    return [];
  }
}

export function dequeue(id: string): void {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(getQueue().filter((i) => i.id !== id)));
}

export async function flushQueue(
  submit: (item: QueuedCompletion) => Promise<void>,
): Promise<void> {
  if (!navigator.onLine) return;
  for (const item of getQueue()) {
    try {
      await submit(item);
      dequeue(item.id);
    } catch {
      // leave in queue; retry on next flush
    }
  }
}
